/**
 * UnitTreeVM.js
 * © EMETL Working Group
 * 
 * This class acts as a View Model for the UnitTree component
 *
 * @author  Andrew Berger
 * @version 1.4.0
 */


import DGUtility from "./DGUtility.m"
import * as queries from "../graphql/queries"
import * as mutations from "../graphql/mutations"

import {
    GraphQLResult,
} from "@aws-amplify/api-graphql"

import db from "./Databases/DGAmplify.m"

import { 
    BranchUser, 
    Unit, 
    User,
} from "../API"

// Site ID 9 is where the mock unit tree is, we're hardcoding that for now
const MOCK_SITE = 10
/**
 * @param u
 * @param u.unitID
 * @param showModal
 * @param refreshUnitTreeUI
 * @param treeData
 * @param depth
 * @param unitID
 * @param needle
 * @param callback
 * @param siteID
 * @since 1.3.0
 * @author Andrew Berger
 */
class UnitTreeTools extends DGUtility{
    #unitTreeViewLoading: (value: boolean) => void

    public constructor() {
        super()
        this.unitTreeViewLoading = () => { /**/ }
    }

    public set unitTreeViewLoading(value: (value: boolean) => void) {
        this.#unitTreeViewLoading = value
    }

    public get unitTreeViewLoading(): (value: boolean) => void {
        return this.#unitTreeViewLoading
    }

    public processUnitTreeData = (data) => {
        const treeMap: {
            [key: string]: {
                unitID: number,
                depth: number,
                immediateChildren: string[],
                branches?: number[],
                name: string,
                parentUnitID?: number,
                leaderID?: string,
            },
        } = {
            "0": {
                unitID: 0,
                depth: 0,
                immediateChildren: [],
                name: `Site ${MOCK_SITE}`,
            }
        }
        
        !!data?.length && typeof(data) === "object" && data?.forEach(
            (unitBranch) => {
                const unitIDIndex = unitBranch.unitID.toString()
                if (!treeMap[unitIDIndex]) {
                    treeMap[unitIDIndex] = {
                        name: unitBranch.name,
                        unitID: unitBranch.unitID,
                        depth: unitBranch.depth,
                        branches: [unitBranch.branchID],
                        parentUnitID: unitBranch.parentUnitID,
                        leaderID: unitBranch.leaderID,
                        immediateChildren: []
                    }
                } else {
                    treeMap[unitBranch.unitID].branches.push(unitBranch.branchID)
                }
            }
        )
        
        Object.values(treeMap)?.forEach(u => {
            if (u.unitID !== 0) {
                const parentID = u.parentUnitID ?? 0
                if (treeMap[parentID.toString()]) {
                    treeMap[parentID.toString()].immediateChildren.push(u.unitID.toString())
                }
            }
        })

        return treeMap
    }
    
    public addUnit = (u, showModal) => {
        const {
            unit,
            unitName
        } = u
        const unitMessage = unit?.name ? `Unit Under ${unit.name}` : "Root Unit"
        showModal({
            title: `Add ${unitMessage} - ${unitName}`,
            data: {
                unitName,
                unitID: unit?.unitID ?? -1,
                unit,
            },
            finalAction: (addUnitData) => {
                const {
                    depth, parentID, leaderID, callback
                } = addUnitData
                this.createUnit(
                    unitName, 
                    depth, 
                    parentID, 
                    leaderID, 
                    depth > 1 ? callback : () => window.location.href = "/",
                )
            },
            actionLabel: "Add Unit",
            searchBarText: "Search for a Unit Leader",
            searchSelectionLabel: "Leader",
            onSearchSubmit: this.searchUsers,
        })
    }

    public renameUnit = async (u, refreshUnitTreeUI) => {
        const {
            unitName,
            unit,
        } = u
        this.unitTreeViewLoading(true)
        const resp = await this.updateUnit(unit.unitID, unitName, unit.depth, unit.leaderID, unit.parentUnitID)
        refreshUnitTreeUI()
        this.unitTreeViewLoading(false)
    }

    public deleteUnitConfirm = ({ unit }, showModal) => {
        if (unit?.branches.length > 1) {
            alert(
                "This unit has subunits and cannot be deleted. Please delete or transfer all subunits before deleting this unit."
            )
            return
        }
        showModal({
            title: "Delete Unit",
            data: {
                unitID: unit.unitID,
                unit,
            },
            bodyContent: "Are you sure you want to delete @@UNIT_NAME?",
            finalAction: (d)=> {
                this.deleteUnit({ 
                    ID: d?.fromUnitID,
                    Depth: d?.fromUnitDepth,
                }).then(d?.callback)
            },
            actionLabel: "Confirm"
        })
    }

    public transferUnit = (unit, showModal) => {
        showModal({
            title: "Transfer Unit",
            data: {
                unit
            },
            searchBarText: "Search for a Unit to Transfer Unit Under",
            onSearchSubmit: this.searchUnits,
            fromUnitText: "Transfer unit",
            searchSelectionLabel: "To unit",
            finalAction: (data) => {
                const {
                    callback,
                    fromUnitDepth,
                    fromUnitID,
                    searchSelection,
                } = data

                this.transfer(callback, fromUnitDepth, fromUnitID, searchSelection.Depth, searchSelection.ID)
                alert("The unit has been transferred successfully")
            },
            actionLabel: "Transfer"
        })
    }

    public removeUserFromBranch = async (userID: number, branchID: number) => {
        const res = await db.executeGQL(
            "deleteBranchUser", {
                BranchID: branchID,
                UserID: userID,
            }
        )

        return res
    }


    public addPersonnel = ({ unit }, showModal) => {
        showModal({
            title: "Add Personnel",
            onSearchSubmit: this.searchUsers,
            data: {
                unitID: unit.unitID,
                unit,
                listPersonnel: true,
            },
            searchBarText: "Search for Site Personnel to Add",
            searchSelectionLabel: "Add Personnel",
            finalAction: async (data) => {
                const {
                    callback,
                    userID,
                    fromUnitDepth,
                } = data
                const b = await this.branchIDForUnit(unit.unitID, fromUnitDepth)
                const res = b?.result
                if (res && !!res.length) {
                    try {
                        const branchIDRes = JSON.parse(res)
                        const branchID = branchIDRes?.body?.result[0]?.BranchID
                        await this.addBranchUser(branchID, userID)
                        callback()
                    } catch (e) {
                        console.error(e)
                    }
                }                
            },
            actionLabel: "Save"
        })
    }

    public partialUnitTreeViewContextMenuActions = (u, showModal, refreshUnitTreeUI) => {
        return {
            addUnit: () => this.addUnit(u, showModal),
            deleteUnit: () => this.deleteUnitConfirm(u, showModal),
            renameUnit: () => this.renameUnit(u, refreshUnitTreeUI),
            transferUnit: () => this.transferUnit(u, showModal),
            addPersonnel: () => this.addPersonnel(u, showModal),
        }
    }

    public splitByColumn = (treeData: {
        depth: number,
    }[], depth: number) => {
        const splitTree = [[], [], []]
        Object.keys(treeData).forEach(
            unitID => {
                const unit = treeData[unitID]
                if (unit.depth > 0) {
                    splitTree[unit.depth - depth].push(unit)
                }
            }
        )
        return splitTree
    }

    public addBranchUser = async (
        branchID: number, 
        userID: number
    ): Promise<GraphQLResult<BranchUser>> => {
        return await db.executeGQL("createBranchUser", { 
            createBranchUserInput: {
                BranchID: branchID,
                UserID: userID,
            } 
        }) as GraphQLResult<BranchUser>
    }

    public createUnit = async (
        name: string, 
        depth: number, 
        parentID: number, 
        leaderID: number, 
        setter: (res: any) => void,
        siteID: number = MOCK_SITE,
    ) => {
        const resp = await db.executeGQL("createUnit", { 
                createUnitInput: {
                    Depth: depth,
                    Name: name,
                    LeaderID: leaderID,
                    ParentUnitID: parentID ?? 0,
                } 
            }) as Unit
        
        const { 
            ID,
        } = resp

        const res = await db.executeLambda(
            "UnitTreeTools.insertUnit",
            {
                depth: depth === 1 ? depth : depth - 1,
                unitID: ID,
                parentUnitID: parentID,
                siteID,
            },
        )

        setter(res)
    }
    
    public transfer = async (
        callback: () => void, 
        fromDepth: number, 
        fromID: number, 
        toDepth: number, 
        toID: number, 
        siteID: number = MOCK_SITE
    ) => {
        const _res = await db.executeLambda(
            "UnitTreeTools.transferUnit",
            {
                fromUnitDepth: fromDepth,
                fromUnitID: fromID,
                toUnitDepth: toDepth,
                toUnitID: toID,
                siteID: siteID,
            },
        )
        callback()
    }
    
    public deleteUnit = async ({ ID, Depth }) => {
        const res = await db.executeLambda(
            "UnitTreeTools.deleteUnit",
            {
                depth: Depth,
                unitID: ID,
                siteID: MOCK_SITE,
            }        
        )

        return res
    }

    public listPersonnelAtUnit = async (ID: number, Depth: number) => {
        const res = await db.executeLambda(
            "UnitTreeTools.listPersonnel",
            {
                depth: Depth,
                unitID: ID,
                siteID: MOCK_SITE,
            }
        )

        return res
    }

    public branchIDForUnit = async (ID: number, Depth: number) => {
        const res = await db.executeLambda(
            "UnitTreeTools.branchIDForUnit",
            {
                depth: Depth,
                unitID: ID,
                siteID: MOCK_SITE,
            }
        )

        return res
    }
    
    /**
     * Update the Unit on the backend.
     * 
     * @function
     * @param {number} unitID - The Unit ID to update
     * @param {string} unitName - The Unit's new name
     * @param {number} unitDepth - The Unit's depth in the tree
     * @param {string} leaderID - The Cognito User ID of the Unit Leader
     * @param {number} parentUnitID - The Parent Unit ID
     * @returns {Promise<GraphQLResult<{ updateUnit: Unit }>>} - The updated Unit
     */
    public async updateUnit(
        unitID: number, 
        unitName: string, 
        unitDepth: number, 
        leaderID: string, 
        parentUnitID: number
    ): Promise<GraphQLResult<{ updateUnit: Unit }>> {
        const updateUnitInput =  {
            ID: unitID,
            Name: unitName,
            Depth: unitDepth,
            ParentUnitID: parentUnitID || 0,
            LeaderID: leaderID || "",
        }

        return await db.executeGQL("updateUnit", {
                updateUnitInput,
            }
        ) as GraphQLResult<{
            updateUnit: Unit
        }>
    }
    
    /**
     * Load a Unit by its ID.
     * 
     * @param {number} unitID - The Unit ID to load
     * @returns {Promise<{ depth: number, name: string, parentUnitID: number, unitID: number } || {}>} The loaded Unit data
     */
    public loadUnit = async (unitID: number): Promise<{ 
        depth: number, name: string, parentUnitID: number, unitID: number 
    } | {}> => {
        debugger
        const getUnit = { 
            ID: unitID,
            BranchID: -1,
        }
        const graphqlOp = await db.executeGQL("getUnit", getUnit)
        
        const response = graphqlOp
        return typeof response === "object" 
            ? {
                depth: response.Depth,
                name: response.Name,
                parentUnitID: response.ParentUnitID,
                unitID
            } : {}
    }

    searchOptions = ["Name", "Leader"]

    public searchUnits = async (
        needle: string, 
        callback: (res: object) => void, 
        siteID = MOCK_SITE,
    ) => {
        try {
            const res = await db.executeLambda(
                "UnitTreeTools.searchForUnit",
                {
                    name: needle,
                    siteID: siteID,
                }
            )
            if (typeof res === "object" && res?.result) {
                const parsedRes = JSON.parse(res.result)
                const resResponse = parsedRes.body?.result
                callback(resResponse)

                return resResponse
            }
        } catch (e) {
            console.error(e)
        }

        return null
    }

    public searchUsers = async (
        needle: string, 
        callback: (res: object) => void, 
        siteID: number = MOCK_SITE
    ) => {
        const users = await db.executeGQL(
            "listUsers"
        )
        const result = []
        users.forEach(
            user => {
                const tokenizedString = `${user?.Name?.toLocaleLowerCase()} ${user?.Email?.toLocaleLowerCase()}`
                if (tokenizedString.search(`${needle}`.toLocaleLowerCase()) > -1)
                    result.push(user)
            }
        )

        callback(result)
    }

    public unitPath = async (
        unitID: number, 
        depth: number, 
        callback: (paths: { Name: string }[]) => void, 
        siteID = MOCK_SITE
    ) => {
        const searchResult = await db.executeLambda(
            "UnitTreeTools.getBranch",
            {
                unitID,
                depth,
                siteID,
            }
        )
        if (typeof searchResult === "object" && searchResult.result) {
            const parsedRes = JSON.parse(searchResult.result)
            callback(parsedRes[0])
        } else {
            callback([])
        }

        return searchResult
    }

    public loadUnits = async (fromDepth, parentUnitID, setter, loading = () => {/**/}) => {
        // loading(true)
        const units = await db.executeLambda(
            "UnitTreeTools.getUnitTree",
            {
                parentUnitID: parentUnitID,
                fromDepth: fromDepth,
                siteID: MOCK_SITE,
            }
        )
        if (units && units?.result && !!units?.result?.length) {
            try {
                const result = JSON.parse(units.result)
                setter(result.body)
            } catch (error) {
                setter(JSON.stringify(error))
            }
            
        } else {
            console.log(`Found no children on parent unit ${parentUnitID}`)
            setter(false)
        }
        // loading(false)
    }
    
    public loadRoot = async (setter, loading = () => {/**/}) => {
     
        // loading(true)
        const units = await db.executeLambda(
            "UnitTreeTools.getUnitTree",
            {
                fromDepth: 0,
                siteID: MOCK_SITE,
            }
        )
        if (units && units?.result && !!units.result.length) {
            const root = {
                id: null,
                parentID: null,
                name: `Site ${MOCK_SITE}`,
            }
            setter(root)
            // loading(false)
        } else {
            console.error("No units found!")
            setter(false)
            // loading(false)
        }
    }
    
    public loadFullTree = async (setter, loading = () => {/**/}) => {
        const units = await db.executeLambda(
            "UnitTreeTools.getUnitTree",
            {
                getFullTree: true,
                fromDepth: 1,
                siteID: MOCK_SITE,
            },
        )
        if (units && units?.result && !!units.result.length) {
            const result = JSON.parse(units.result)
            const processedData = this.processUnitTreeData(result?.body)
            setter(processedData)
        } else {
            console.error("No units found!")
        }
    }
}

export default UnitTreeTools
